import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { Map } from 'immutable'
import _get from 'lodash/get'
import { compose } from 'recompose'
import { connect as connectRedux } from 'react-redux'
import {
  AdyenCheckout,
  Dropin,
  Card,
  PayPal,
  GooglePay,
  ApplePay,
} from '@adyen/adyen-web'
import FormsyForm from 'formsy-react'
import { getBoundActions } from '../../actions/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import {
  adyenDropInConfiguration,
  adyenOptionalConfiguration,
  adyenPaypalActive,
  adyenApplepayActive,
  adyenGooglepayActive,
  adyenPaymentMethodsConfiguration,
} from '../../services/adyen/index'
import { getPrimary, getLanguageLocale } from '../../services/languages/index'
import { get as getConfig } from '../../config/index'
import Sherpa, { TYPE_SMALL_WHITE } from '../Sherpa/index'
import Button, { TYPES } from '../Button/index'
import { getElementToWindowTopOffset } from '../../services/dom/index'
import { US } from '../../services/country/index'
import { USD } from '../../services/currency/index'
import { NumberInput } from '../FormInput.v2/index'
import Link from '../Link/index'
import { TARGET_BLANK } from '../Link/constants'
import IconV2, { ICON_TYPES } from '../Icon.v2/index'

const configuration = getConfig()
const adyenTestingActive = !!_get(configuration, ['features', 'development', 'adyenTesting'])
let dropin = {}
let submit = () => null

function getAdyenDropInClassName (stateProvinceShown) {
  const baseClassName = 'adyen-drop-in'
  const className = [baseClassName]

  if (stateProvinceShown) {
    className.push(`${baseClassName}--state-province-shown`)
  }

  if (adyenPaypalActive || adyenApplepayActive || adyenGooglepayActive) {
    className.push(`${baseClassName}--multi-pay`)
  }

  return className.join(' ')
}

function getAdyenDropInFormClassName (options) {
  const {
    visible,
  } = options
  const baseClassName = 'adyen-drop-in__payment-form'
  const className = [baseClassName]

  if (visible) {
    className.push(`${baseClassName}--visible`)
  }

  return className.join(' ')
}

function getAdyenDropInWrapperClassName () {
  const baseClassName = 'adyen-drop-in__payment-wrapper'
  const className = [baseClassName]

  if (adyenPaypalActive || adyenApplepayActive || adyenGooglepayActive) {
    className.push(`${baseClassName}--multi-pay`)
  }

  return className.join(' ')
}

async function createCheckout (options) {
  const {
    ref,
    config,
    optionalConfig,
    staticText,
    country,
    setAdyenStateProvinceShown,
    setPaymentBillingInfo,
    currencyIso,
    planCost,
  } = options
  const checkout = await AdyenCheckout(config)

  if (ref.current) {
    dropin = new Dropin(checkout, {
      ...optionalConfig,
      paymentMethodComponents: [Card, PayPal, GooglePay, ApplePay],
      paymentMethodsConfiguration: adyenPaymentMethodsConfiguration({
        cardName: staticText.get('creditOrDebit'),
        country,
        setAdyenStateProvinceShown,
        setPaymentBillingInfo,
        currencyIso,
        planCost,
      }),
    }).mount(ref.current)

    submit = dropin && dropin.submit ? dropin.submit : () => null
  }
}

/**
 * @type {React.Component} Adyen drop in component error message
 */
function AdyenDropInErrorMessage (props) {
  const { children } = props
  return (
    <div className="adyen-drop-in__error-message">
      {children}
    </div>
  )
}

function AdyenDropIn (props) {
  const {
    auth,
    user,
    adyen,
    payment,
    plans,
    userAccount,
    initAdyen,
    getAdyenPaymentAuthorization,
    getAdyenPaymentAuthorizationDetails,
    setAdyenStateProvinceShown,
    setAdyenDropInReady,
    setAdyenDropInError,
    staticText,
    submitButtonText,
    disclaimer,
    error,
    parentProcessing,
    velocityCheckSuccess,
    resetAdyen,
    resetPaymentMethods,
    isResetOnUnmount,
    isGift,
    planCost,
    setPaymentTestCode,
    resetPaymentTestCode,
    setPaymentBillingInfo,
  } = props
  const paymentContainer = useRef(null)
  /** @type {Boolean|undefined} undefined until the state is ready for payment methods */
  const paymentMethodsSuccess = payment.getIn(['data', 'paymentMethods', 'success'])
  const paymentMethods = payment.getIn(['data', 'paymentMethods', 'data']) || Map()
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const stateProvinceShown = adyen.get('stateProvinceShown')
  const visible = adyen.get('ready') && velocityCheckSuccess !== false
  const processing = adyen.get('processing')
  const paymentsDataProcessing = payment.get('paymentDataProcessing')
  const scrollToError = error && !parentProcessing && !paymentsDataProcessing
  const selectedPlan = plans.get('selection', Map())
  const userAccountSubscriptions = userAccount.getIn(['details', 'data', 'billing', 'subscriptions'], Map())
  const userRequestCountry = user.getIn(['data', 'requestCountry'], US)
  const authCountry = auth.get('country')
  const userAccountCountry = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'countryIso'])
  const country = userAccountCountry || authCountry || userRequestCountry || US
  const currencyIso = userAccountSubscriptions.get('currencyIso')
    || selectedPlan.get('currencyIso')
    || USD
  const [testingVisible, setTestingVisible] = useState(false)
  const testResultCode = payment.getIn(['data', 'testResultCode'], '')

  // initialize Adyen Drop In
  useEffect(() => {
    initAdyen()

    return () => {
      // reset adyen and payment data
      if (isResetOnUnmount) {
        resetAdyen()
        resetPaymentMethods()
      }
    }
  }, [isResetOnUnmount])

  // Instantiate the Adyen Drop In
  useEffect(() => {
    if (!paymentMethodsSuccess || paymentMethods.size === 0 || !paymentContainer.current) {
      // data or Dom is not ready
      return
    }

    createCheckout({
      ref: paymentContainer,
      config: adyenDropInConfiguration({
        paymentMethodsResponse: paymentMethods.toJS(),
        getAdyenPaymentAuthorization,
        getAdyenPaymentAuthorizationDetails,
        setAdyenStateProvinceShown,
        setAdyenDropInError,
        setPaymentBillingInfo,
        submitButtonText,
        locale: getLanguageLocale(userLanguage),
        country,
        currencyIso,
        cardName: staticText.get('creditOrDebit'),
        isGift,
        planCost,
      }),
      optionalConfig: adyenOptionalConfiguration({
        setAdyenDropInReady,
      }),
      staticText,
      country,
      setAdyenStateProvinceShown,
      setPaymentBillingInfo,
      currencyIso,
      planCost,
    })
  }, [paymentMethods, paymentContainer])

  useEffect(() => {
    // scroll to the top of iframe so if there is an error it will be visible
    if (scrollToError && global && global.scrollTo) {
      const offset = getElementToWindowTopOffset(paymentContainer.current) - 250 || 0
      requestAnimationFrame(() => global.scrollTo(0, offset))
    }

    if (scrollToError && dropin && dropin.setStatus) {
      // reset the iframe status
      dropin.setStatus('ready')
    }
  }, [scrollToError])

  // If we have a failed velocity check, remove the adyen dropin from the DOM
  // to prevent anyone from finding it
  useEffect(() => {
    if (
      velocityCheckSuccess === false
        && paymentContainer.current
        && paymentContainer.current.firstChild
    ) {
      paymentContainer.current.remove(paymentContainer.current.firstChild)
    }
  }, [velocityCheckSuccess])

  if (!getAuthIsLoggedIn(auth)) {
    return null
  }

  return (
    <div className={getAdyenDropInClassName(stateProvinceShown)}>
      {
        processing
          ? <Sherpa type={TYPE_SMALL_WHITE} aria-label="credit card form loading" />
          : null
      }
      {
        // Only show errors if we didn't allow payment iframe anyway
        !processing && velocityCheckSuccess === false
          ? <div className="adyen-drop-in">
            <AdyenDropInErrorMessage>
              {staticText.getIn(['somethingWentWrong'])}
            </AdyenDropInErrorMessage>
          </div>
          : null
      }
      <div className={getAdyenDropInWrapperClassName()}>
        <div
          ref={paymentContainer}
          className={getAdyenDropInFormClassName({ visible })}
        />
      </div>
      {
        visible && disclaimer
          ? <div className="adyen-drop-in__disclaimer">
            {disclaimer}
          </div>
          : null
      }
      {
        visible && !adyenPaypalActive && !adyenApplepayActive && !adyenGooglepayActive
          ? <Button
              type={TYPES.PRIMARY}
              onClick={submit}
              className="adyen-drop-in__submit"
              disabled={processing || paymentsDataProcessing || parentProcessing}
            >
              {submitButtonText}
            </Button>
          : null
      }
      {
        visible && adyenTestingActive
          ? <div className="adyen-drop-in__testing">
              <Button
                type={TYPES.LINK}
                className="adyen-drop-in__testing-toggle"
                onClick={() => setTestingVisible(!testingVisible)}
              >
                {testingVisible ? 'Hide Adyen Payment Testing ' : 'Show Adyen Payment Testing '}
                <IconV2
                  className="adyen-drop-in__testing-toggle-icon"
                  type={testingVisible ? ICON_TYPES.CHEVRON_UP : ICON_TYPES.CHEVRON_DOWN}
                />
              </Button>
              {
                <React.Fragment>
                  <FormsyForm
                    className={`adyen-drop-in__testing-form${testingVisible ? ' adyen-drop-in__testing-form--visible' : ''}`}
                    onValidSubmit={(v) => setPaymentTestCode(v.adyenTestCode)}
                  >
                    <NumberInput
                      value={testResultCode}
                      label={'Enter Payment Result Code'}
                      className="adyen-drop-in__testing-field"
                      name="adyenTestCode"
                      maxLength={2}
                      validations={{
                        isNumeric: true,
                        maxLength: 2,
                      }}
                      validationErrors={{
                        isNumeric: 'A two digit number is required',
                        maxLength: 'A maximum of 2 digits is allowed',
                      }}
                      validationTimeout={0}
                      required
                    />
                    <Button
                      submit
                      className="adyen-drop-in__testing-button"
                      type={TYPES.PRIMARY}
                    >
                      Set Result Code
                    </Button>
                    <Button
                      className="adyen-drop-in__testing-button adyen-drop-in__testing-button--last"
                      type={TYPES.SECONDARY}
                      onClick={() => resetPaymentTestCode()}
                    >
                      Clear Result Code
                    </Button>
                    <Link
                      className="adyen-drop-in__code-link"
                      to={'https://docs.adyen.com/development-resources/testing/result-code-testing/adyen-response-codes'}
                      directLink
                      target={TARGET_BLANK}
                    >
                      Adyen Payment Result Test Codes
                    </Link>
                  </FormsyForm>
                </React.Fragment>
              }
            </div>
          : null
      }
    </div>
  )
}

AdyenDropIn.propTypes = {
  disclaimer: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  submitButtonText: PropTypes.string.isRequired,
  parentProcessing: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  isResetOnUnmount: PropTypes.bool,
  isGift: PropTypes.bool,
  planCost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  initAdyen: PropTypes.func.isRequired,
  getAdyenPaymentAuthorization: PropTypes.func.isRequired,
  getAdyenPaymentAuthorizationDetails: PropTypes.func.isRequired,
  setAdyenStateProvinceShown: PropTypes.func.isRequired,
  setAdyenDropInReady: PropTypes.func.isRequired,
  setAdyenDropInError: PropTypes.func.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  adyen: ImmutablePropTypes.map.isRequired,
  payment: ImmutablePropTypes.map.isRequired,
  plans: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  velocityCheckSuccess: PropTypes.bool,
  velocityCheckProcessing: PropTypes.bool,
  velocityCheckToken: PropTypes.string,
  resetAdyen: PropTypes.func.isRequired,
  resetPaymentMethods: PropTypes.func.isRequired,
  setPaymentTestCode: PropTypes.func,
  resetPaymentTestCode: PropTypes.func,
  setPaymentBillingInfo: PropTypes.func.isRequired,
}

AdyenDropIn.defaultProps = {
  isResetOnUnmount: true,
  isGift: false,
  planCost: 0,
}

export default compose(
  connectRedux(
    (state) => ({
      auth: state.auth,
      user: state.user,
      adyen: state.adyen,
      plans: state.plans,
      userAccount: state.userAccount,
      payment: state.payment,
      staticText: state.staticText.getIn(['data', 'adyenDropIn', 'data']),
      velocityCheckProcessing: state.velocityCheck.get('velocityCheckProcessing'),
      velocityCheckSuccess: state.velocityCheck.getIn(['data', 'velocityCheck', 'success']),
      velocityCheckToken: state.velocityCheck.getIn(['data', 'velocityCheck', 'token']),
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        initAdyen: actions.adyen.initAdyen,
        getAdyenPaymentAuthorization: actions.adyen.getAdyenPaymentAuthorization,
        getAdyenPaymentAuthorizationDetails: actions.adyen.getAdyenPaymentAuthorizationDetails,
        setAdyenStateProvinceShown: actions.adyen.setAdyenStateProvinceShown,
        setAdyenDropInReady: actions.adyen.setAdyenDropInReady,
        setAdyenDropInError: actions.adyen.setAdyenDropInError,
        resetAdyen: actions.adyen.resetAdyen,
        setPaymentBillingInfo: actions.payment.setPaymentBillingInfo,
        resetPaymentMethods: actions.payment.resetPaymentMethods,
        setPaymentTestCode: actions.payment.setPaymentTestCode,
        resetPaymentTestCode: actions.payment.resetPaymentTestCode,
      }
    },
  ),
)(AdyenDropIn)
